import { useMemo, useState } from 'react'

import { StructureTreeItem } from '../../types'
import { sortTreeItems } from './treeDataUtils'
import { UseTreeDataOptions, UseTreeDataReturn } from './types'
import { useTreeDataLoading } from './useTreeDataLoading'
import { useTreeDataOperations } from './useTreeDataOperations'

export const useTreeData = ({
	onChildrenLoaded,
	onBatchLoaded,
	alphabetSort = true,
	initialChildrenMap,
	initialSystems,
	initialExpandedIds: _initialExpandedIds,
	selectedSystemId = null,
}: UseTreeDataOptions): UseTreeDataReturn => {
	// Core state – seed from Redux when returning from another page (e.g. Settings)
	const [rawSystems, setRawSystems] = useState<StructureTreeItem[]>(initialSystems ?? [])
	const [rawChildrenMap, setRawChildrenMap] = useState<
		Record<string, StructureTreeItem[]>
	>(initialChildrenMap ?? {})
	const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
	const [usedIds, setUsedIds] = useState<Set<string>>(() =>
		new Set([
			...(initialSystems ?? []).map((s) => s.id),
			...Object.values(initialChildrenMap ?? {}).flat().map((c) => c.id),
		])
	)
	const [pendingRefreshParentIds, setPendingRefreshParentIds] = useState<
		Set<string>
	>(() => new Set(Object.keys(initialChildrenMap ?? {})))
	// No skeleton when we have cached systems (e.g. back from Settings)
	const [isFullTreeLoading, setIsFullTreeLoading] = useState(initialSystems?.length === 0)
	const [initialLoadDone, setInitialLoadDone] = useState(false)

	// Compose state object for sub-hooks
	const state = {
		rawSystems,
		setRawSystems,
		rawChildrenMap,
		setRawChildrenMap,
		loadingIds,
		setLoadingIds,
		usedIds,
		setUsedIds,
		pendingRefreshParentIds,
		setPendingRefreshParentIds,
		isFullTreeLoading,
		setIsFullTreeLoading,
		initialLoadDone,
		setInitialLoadDone,
	}

	// Loading effects
	const { isTreeLoading } = useTreeDataLoading(state, {
		onBatchLoaded,
	})

	// Memoized sorted values — keep full list for reveal, filter for display
	const allSystems = useMemo(
		() => sortTreeItems(rawSystems, alphabetSort),
		[rawSystems, alphabetSort],
	)

	const childrenMap = useMemo(() => {
		const sorted: Record<string, StructureTreeItem[]> = {}
		for (const [key, children] of Object.entries(rawChildrenMap)) {
			sorted[key] = sortTreeItems(children, alphabetSort)
		}
		return sorted
	}, [rawChildrenMap, alphabetSort])

	// Show children of the selected system only. Never fall back to all systems —
	// that caused a flash of every root system while selection was still resolving.
	const systems = useMemo(() => {
		if (!selectedSystemId) return []
		return childrenMap[selectedSystemId] ?? []
	}, [childrenMap, selectedSystemId])

	// Operations
	const {
		loadChildren,
		refresh,
		refreshSystems,
		refreshSystemsSilent,
		revealItem,
		findItem,
	} = useTreeDataOperations(state, {
		onChildrenLoaded,
		onBatchLoaded,
		systems: allSystems,
		childrenMap,
	})

	return {
		systems,
		childrenMap,
		loadingIds,
		pendingRefreshParentIds,
		usedIds,
		isTreeLoading,
		isFullTreeLoading,
		loadChildren,
		refresh,
		refreshSystems,
		refreshSystemsSilent,
		revealItem,
		findItem,
		setChildrenMap: setRawChildrenMap,
		setRawSystems,
		setUsedIds,
	}
}

